import React, { useEffect, useContext, useReducer } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import Seo from 'components/common/Seo';
import Header from 'components/section/Header';
import MobileNavigation from 'components/section/MobileNavigation';
import LoginForm from 'components/common/LoginForm';
import GlobalContext from 'components/layout/GlobalContext';
import useHasMounted from 'hooks/useHasMounted';
import useServiceWorkerUpdate from 'hooks/useServiceWorkerUpdate';
import authenticationCtx from 'context/authentication';
import darkmodeCtx from 'context/darkmode'; // in this stage, it is not yet bacome part of the ContextProvider
import feedbackCtx from 'context/feedback';
import numericFeedbackCtx from 'context/numericfeedback';
import backdropCtx from 'context/backdrop';

const Layout = ({ pageContext = {}, ...props }) => (
	<GlobalContext pageProps={props}>
		<LayoutInner {...props} />
	</GlobalContext>
);

const LayoutInner = ({ children, ...props }) => {
	const hasMounted = useHasMounted();
	useServiceWorkerUpdate();
	const { initialized, isLoggedIn } = useContext(authenticationCtx);
	const { darkmode } = useContext(darkmodeCtx);
	const { feedbackMsg, feedbackHasBackdrop } = useContext(feedbackCtx);
	const { numericKeypadContent } = useContext(numericFeedbackCtx);
	const { backdrop } = useContext(backdropCtx);
	const [_, forceUpdate] = useReducer(x => x + 1, 0);

	const pageTitle = 'ITSA Trading App';
	const pageDescription = 'ITSA Trading App';

	let pageContent = <LoginForm {...props} />; // in case a non admin tries to access an adminpage directly by its url:

	useEffect(() => {
		forceUpdate();
	}, [isLoggedIn]);

	if (!initialized) {
		return <div className="fixed top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 text-lg" />;
	}

	if (isLoggedIn) {
		pageContent = children;
	}

	let backdropLayer;
	if (backdrop) {
		backdropLayer = <div className="fixed inset-0 bg-black opacity-50 z-40" />;
	}

	let feedbackLayer;
	if (feedbackMsg) {
		let feedbackBackdrop;
		if (feedbackHasBackdrop) {
			feedbackBackdrop = <div className="fixed inset-0 bg-black opacity-50 z-40" />;
		}
		feedbackLayer = (
			<>
				{feedbackBackdrop}
				<div className="fixed inset-0 z-50">{feedbackMsg}</div>
			</>
		);
	}

	let numericKeypadLayer;
	if (numericKeypadContent) {
		numericKeypadLayer = (
			<>
				<div className="fixed inset-0 bg-black opacity-40 z-40" />
				<div className="fixed inset-0 z-50">{numericKeypadContent}</div>
			</>
		);
	}

	return (
		<div className="flex min-h-screen flex-col gap-y-16 max-md:h-[var(--screen-height)] max-md:min-h-0 max-md:gap-y-0 max-md:overflow-hidden max-md:pt-[env(safe-area-inset-top)]">
			{backdropLayer}
			{feedbackLayer}
			{numericKeypadLayer}
			<Seo title={pageTitle} description={pageDescription} />
			<Header {...props} />
			<main className="container flex grow-1 flex-col gap-y-16 max-md:min-h-0 max-md:gap-y-4 max-md:overflow-y-auto max-md:overflow-x-hidden max-md:overscroll-y-contain max-md:py-3">
				{pageContent}
				{feedbackMsg}
			</main>
			<MobileNavigation />
		</div>
	);
};

Layout.propTypes = {
	children: PropTypes.node.isRequired,
	pageContext: PropTypes.object,
};

LayoutInner.propTypes = {
	children: PropTypes.node.isRequired,
};

export default Layout;
